import { produce } from 'immer'
import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { useHighlightCells } from '~/hooks/useHighlightCells'
import { type Brand } from '~/hooks/useSurveyQuery'
import { useTamRatio } from '~/hooks/useTamRatio'
import {
  calcHeatmapRatio,
  type CellInfo,
  compact,
  exists,
  findByObj,
  findByPath,
  type FiveSegment,
  type FiveSegmentIndicator,
  fiveSegmentIndicators,
  getDiffKey,
  getSortIconBySortValue,
  mapFiveSegment,
  mapItemsToObj,
  mapNineSegment,
  type NineSegment,
  type NineSegmentIndicator,
  nineSegmentIndicators,
  NumFormat,
  QuestionOp,
  reject,
  type RowInfo,
} from '~/utils'

import {
  columnsDiffsAtom,
  dataDisplayTypeAtom,
  hiddenRowIdsAtom,
  segmentDisplayTypeAtom,
  sortValueAtom,
} from '../../atoms'
import {
  type ColumnKey,
  type CountColumnKey,
  type DiffColumnKey,
  getCountColumnLabel,
  getDiffColumnLabel,
  indicatorsForFiveSegs,
  indicatorsForNineSegs,
  indicatorsForSummary,
} from '../../utils'
import { useAnswersArrayData } from '../useAnswersArrayData'
import { useCustomerAttributesArrayData } from '../useCustomerAttributesArrayData'
import { useFiveSegmentsBrandsData } from '../useFiveSegmentsBrandsData'
import { useIndicatorLabelMap } from '../useIndicatorLabelMap'
import { useMapCountColumnKey } from '../useMapCountColumnKey'
import { useMapDiffColumnKey } from '../useMapDiffColumnKey'
import { useNineSegmentsBrandsData } from '../useNineSegmentsBrandsData'
import { useTabulatedBrands } from '../useTabulatedBrands'
import { useTabulatedColumnCustomerAttributes } from '../useTabulatedColumnCustomerAttributes'
import { useTotalCountsData } from '../useTotalCountsData'

import { TitleRowPopup } from './TitleRowPopup'

export const useTableInfo = (): RowInfo<ColumnKey>[] => {
  const { t } = useTranslation()
  const customizedSegmentsLabel = t('customized_segments')
  const occurrenceRateLabel = t('occurrence_rate')
  const sampleSizeLabel = t('sample_size')
  const columnCustomerAttributes = useTabulatedColumnCustomerAttributes()
  const mapCountColumnKey = useMapCountColumnKey()
  const mapDiffColumnKey = useMapDiffColumnKey()
  const { addHighlightProps } = useHighlightCells('customer-attribute-analysis')
  const indicatorLabelMap = useIndicatorLabelMap()

  const tamRatio = useTamRatio()
  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const formatCount = React.useCallback(
    (count: number, ratio: number) =>
      NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
    [dataDisplayType, tamRatio],
  )
  const formatForDiff = React.useCallback(
    (count: number, ratio: number) =>
      NumFormat.countToPts(count, ratio, tamRatio, dataDisplayType),
    [dataDisplayType, tamRatio],
  )

  const brands = useTabulatedBrands()
  const fiveSegmentsBrandsData = useFiveSegmentsBrandsData()
  const nineSegmentsBrandsData = useNineSegmentsBrandsData()
  const brandRowInfoMap = React.useMemo<
    Record<
      Brand['id'],
      Record<
        FiveSegment | FiveSegmentIndicator | NineSegment | NineSegmentIndicator,
        RowInfo<ColumnKey>
      >
    >
  >(
    () =>
      mapItemsToObj(brands, (brand) => ({
        ...mapFiveSegment((fiveSegment) => {
          const getCountAndRatio = (
            countColumnKey: CountColumnKey,
          ): [count: number, ratio: number] => {
            const brandData = findByObj(fiveSegmentsBrandsData, {
              countColumnKey,
              brandId: brand.id,
            })
            const ratio = brandData?.ratios[fiveSegment] ?? NaN
            const count = brandData?.counts[fiveSegment] ?? NaN

            return [count, ratio]
          }

          return {
            subcategory: {
              isColumnHeader: true,
              isHeader: true,
              text: indicatorLabelMap[fiveSegment],
            },
            ...mapCountColumnKey((countColumnKey) => {
              const [count, ratio] = getCountAndRatio(countColumnKey)
              const text = formatCount(count, ratio)
              const key = `${brand.id}-${fiveSegment}-${countColumnKey}`

              return { ratio, text, ...addHighlightProps(key) }
            }),
            ...mapDiffColumnKey((diffColumnKey, [left, right]): CellInfo => {
              const [leftCount, leftRatio] = getCountAndRatio(left)
              const [rightCount, rightRatio] = getCountAndRatio(right)
              const [text, copyValue] = formatForDiff(
                leftCount - rightCount,
                leftRatio - rightRatio,
              )
              const key = `diff-${brand.id}-${fiveSegment}-${diffColumnKey}`

              return {
                align: 'center',
                copyValue,
                text,
                ...addHighlightProps(key),
              }
            }),
          }
        }),
        ...R.mapToObj(fiveSegmentIndicators, (indicator) => {
          const getRatio = (countColumnKey: CountColumnKey) => {
            const brandData = findByObj(fiveSegmentsBrandsData, {
              countColumnKey,
              brandId: brand.id,
            })

            return brandData?.ratios[indicator] ?? NaN
          }

          return [
            indicator,
            {
              subcategory: {
                isColumnHeader: true,
                isHeader: true,
                text: indicatorLabelMap[indicator],
              },
              ...mapCountColumnKey((countColumnKey) => {
                const ratio = getRatio(countColumnKey)
                const text = NumFormat.forRatio(ratio)
                const key = `${brand.id}-${indicator}-${countColumnKey}`

                return { ratio, text, ...addHighlightProps(key) }
              }),
              ...mapDiffColumnKey((diffColumnKey, [left, right]): CellInfo => {
                const leftRatio = getRatio(left)
                const rightRatio = getRatio(right)
                const [text, copyValue] = NumFormat.ratioToPts(
                  leftRatio - rightRatio,
                )
                const key = `diff-${brand.id}-${indicator}-${diffColumnKey}`

                return {
                  align: 'center',
                  copyValue,
                  text,
                  ...addHighlightProps(key),
                }
              }),
            },
          ]
        }),
        ...mapNineSegment((nineSegment) => {
          const getCountAndRatio = (
            countColumnKey: CountColumnKey,
          ): [count: number, ratio: number] => {
            const brandData = findByObj(nineSegmentsBrandsData, {
              countColumnKey,
              brandId: brand.id,
            })
            const ratio = brandData?.ratios[nineSegment] ?? NaN
            const count = brandData?.counts[nineSegment] ?? NaN

            return [count, ratio]
          }

          return {
            subcategory: {
              isColumnHeader: true,
              isHeader: true,
              text: indicatorLabelMap[nineSegment],
            },
            ...mapCountColumnKey((countColumnKey) => {
              const [count, ratio] = getCountAndRatio(countColumnKey)
              const text = formatCount(count, ratio)
              const key = `${brand.id}-${nineSegment}-${countColumnKey}`

              return { ratio, text, ...addHighlightProps(key) }
            }),
            ...mapDiffColumnKey((diffColumnKey, [left, right]): CellInfo => {
              const [leftCount, leftRatio] = getCountAndRatio(left)
              const [rightCount, rightRatio] = getCountAndRatio(right)
              const [text, copyValue] = formatForDiff(
                leftCount - rightCount,
                leftRatio - rightRatio,
              )
              const key = `diff-${brand.id}-${nineSegment}-${diffColumnKey}`

              return {
                align: 'center',
                copyValue,
                text,
                ...addHighlightProps(key),
              }
            }),
          }
        }),
        ...R.mapToObj(nineSegmentIndicators, (indicator) => {
          const getRatio = (countColumnKey: CountColumnKey) => {
            const brandData = findByObj(nineSegmentsBrandsData, {
              countColumnKey,
              brandId: brand.id,
            })

            return brandData?.ratios[indicator] ?? NaN
          }

          return [
            indicator,
            {
              subcategory: {
                isColumnHeader: true,
                isHeader: true,
                text: indicatorLabelMap[indicator],
              },
              ...mapCountColumnKey((countColumnKey) => {
                const ratio = getRatio(countColumnKey)
                const text = NumFormat.forRatio(ratio)
                const key = `${brand.id}-${indicator}-${countColumnKey}`

                return { ratio, text, ...addHighlightProps(key) }
              }),
              ...mapDiffColumnKey((diffColumnKey, [left, right]): CellInfo => {
                const leftRatio = getRatio(left)
                const rightRatio = getRatio(right)
                const [text, copyValue] = NumFormat.ratioToPts(
                  leftRatio - rightRatio,
                )
                const key = `diff-${brand.id}-${indicator}-${diffColumnKey}`

                return {
                  align: 'center',
                  copyValue,
                  text,
                  ...addHighlightProps(key),
                }
              }),
            },
          ]
        }),
      })),
    [
      addHighlightProps,
      brands,
      fiveSegmentsBrandsData,
      formatCount,
      formatForDiff,
      indicatorLabelMap,
      mapCountColumnKey,
      mapDiffColumnKey,
      nineSegmentsBrandsData,
    ],
  )

  const hiddenRowIds = useAtomValue(hiddenRowIdsAtom)
  const segmentDisplayType = useAtomValue(segmentDisplayTypeAtom)
  const answersArrayData = useAnswersArrayData()
  const customerAttributesArrayData = useCustomerAttributesArrayData()

  const sortValue = useAtomValue(sortValueAtom)
  const titleRowInfo = React.useMemo<RowInfo<ColumnKey>>(
    () => ({
      category: {
        colSpan: 2,
        isColumnHeader: true,
        isHeader: true,
        text: customizedSegmentsLabel,
      },
      ...mapCountColumnKey((countColumnKey): CellInfo => ({
        align: 'center',
        icon: getSortIconBySortValue(sortValue, countColumnKey),
        isColumnHeader: true,
        isHeader: true,
        text: getCountColumnLabel(columnCustomerAttributes, countColumnKey, t),
      })),
      ...mapDiffColumnKey((diffColumnKey, columnsDiff): CellInfo => ({
        align: 'center',
        icon: getSortIconBySortValue(sortValue, diffColumnKey),
        isColumnHeader: true,
        isHeader: true,
        text: getDiffColumnLabel(columnCustomerAttributes, columnsDiff, t),
      })),
    }),
    [
      columnCustomerAttributes,
      customizedSegmentsLabel,
      mapCountColumnKey,
      mapDiffColumnKey,
      sortValue,
      t,
    ],
  )

  const totalCountsData = useTotalCountsData()
  const columnsDiffs = useAtomValue(columnsDiffsAtom)
  const diffMinMaxMap = React.useMemo(
    () =>
      mapDiffColumnKey((diffColumnKey) => {
        const ratioArray = R.pipe(
          R.concat(answersArrayData, customerAttributesArrayData),
          R.flatMap((rowsData) =>
            rowsData.map((rowData) => rowData.ratios[diffColumnKey]),
          ),
          compact,
        )

        return { max: Math.max(...ratioArray), min: Math.min(...ratioArray) }
      }),
    [answersArrayData, customerAttributesArrayData, mapDiffColumnKey],
  )

  return React.useMemo(() => {
    const visibleIndicatorsForFiveSegs = R.difference(
      indicatorsForFiveSegs,
      hiddenRowIds,
    ) as (FiveSegment | FiveSegmentIndicator)[]
    const visibleIndicatorsForNineSegs = R.difference(
      indicatorsForNineSegs,
      hiddenRowIds,
    ) as (NineSegment | NineSegmentIndicator)[]
    const visibleIndicatorsForSummary = R.difference(
      indicatorsForSummary,
      hiddenRowIds,
    ) as (
      FiveSegment | FiveSegmentIndicator | NineSegment | NineSegmentIndicator
    )[]

    const visibleAnswersArrayData = R.pipe(
      answersArrayData,
      R.map(
        reject((answerData) =>
          hiddenRowIds.includes(
            `${answerData.question.question_id}-${answerData.answerOption.option}`,
          ),
        ),
      ),
    )

    const visibleCustomerAttributesArrayData = R.pipe(
      customerAttributesArrayData,
      R.map(
        reject((customerAttributeData) =>
          hiddenRowIds.includes(customerAttributeData.customerAttribute.id),
        ),
      ),
    )

    const countRowInfosArray = compact([
      // 5segs
      (segmentDisplayType === 'fiveSegs' || segmentDisplayType === 'both') &&
        brands.map((brand) =>
          visibleIndicatorsForFiveSegs.map<RowInfo<ColumnKey>>(
            (indicator, indicatorIndex) =>
              produce(brandRowInfoMap[brand.id][indicator], (draft) => {
                if (indicatorIndex === 0) {
                  draft.category = {
                    align: 'center',
                    isColumnHeader: true,
                    isHeader: true,
                    text: `${brand.name}\n5segs`,
                    rowSpan: visibleIndicatorsForFiveSegs.length,
                  }
                }
              }),
          ),
        ),
      // 9segs
      (segmentDisplayType === 'nineSegs' || segmentDisplayType === 'both') &&
        brands.map((brand) =>
          visibleIndicatorsForNineSegs.map<RowInfo<ColumnKey>>(
            (indicator, indicatorIndex) =>
              produce(brandRowInfoMap[brand.id][indicator], (draft) => {
                if (indicatorIndex === 0) {
                  draft.category = {
                    align: 'center',
                    isColumnHeader: true,
                    isHeader: true,
                    text: `${brand.name}\n9segs`,
                    rowSpan: visibleIndicatorsForNineSegs.length,
                  }
                }
              }),
          ),
        ),
      // まとめ
      segmentDisplayType === 'summary' &&
        brands.map((brand) =>
          visibleIndicatorsForSummary.map<RowInfo<ColumnKey>>(
            (indicator, indicatorIndex) =>
              produce(brandRowInfoMap[brand.id][indicator], (draft) => {
                if (indicatorIndex === 0) {
                  draft.category = {
                    align: 'center',
                    isColumnHeader: true,
                    isHeader: true,
                    text: brand.name,
                    rowSpan: visibleIndicatorsForSummary.length,
                  }
                }
              }),
          ),
        ),
      // 設問
      visibleAnswersArrayData.map((answersData) => [
        // 選択肢
        ...answersData.map<RowInfo<ColumnKey>>(
          (answerData, answerDataIndex) => {
            const getCountAndRatio = (
              columnKey: CountColumnKey | DiffColumnKey,
            ): [count: number, ratio: number] => {
              const count = answerData.counts[columnKey]
              const ratio = answerData.ratios[columnKey]

              return [count, ratio]
            }

            return produce(
              {
                subcategory: {
                  isHeader: true,
                  text: answerData.answerOption.text,
                  textCharacterLimit: 30,
                },
                ...mapCountColumnKey((countColumnKey): CellInfo => {
                  const [count, ratio] = getCountAndRatio(countColumnKey)
                  const text = formatCount(count, ratio)
                  const key = `answer-${answerData.question.question_id}-${answerData.answerOption.option}-${countColumnKey}`

                  return {
                    ratio,
                    text,
                    ...addHighlightProps(key),
                  }
                }),
                ...mapDiffColumnKey((diffColumnKey): CellInfo => {
                  const [count, ratio] = getCountAndRatio(diffColumnKey)
                  const [text, copyValue] = formatForDiff(count, ratio)
                  const { max, min } = diffMinMaxMap[diffColumnKey]
                  const key = `diff-${answerData.question.question_id}-${answerData.answerOption.option}-${diffColumnKey}`

                  return {
                    align: 'center',
                    copyValue,
                    heatmap: true,
                    ratio: calcHeatmapRatio(ratio, max, min),
                    text,
                    ...addHighlightProps(key),
                  }
                }),
              } as RowInfo<ColumnKey>,
              (draft) => {
                if (answerDataIndex === 0) {
                  draft.category = {
                    align: 'center',
                    isHeader: true,
                    text: QuestionOp.toText(answerData.question),
                    rowSpan: answerData.question.answer_options.length,
                  }
                }
              },
            )
          },
        ),
      ]),
      visibleCustomerAttributesArrayData.map(
        (visibleCustomerAttributesData) => [
          // 顧客属性
          ...visibleCustomerAttributesData.map<RowInfo<ColumnKey>>(
            (customerAttributeData, customerAttributeDataIndex) => {
              const getCountAndRatio = (
                columnKey: CountColumnKey | DiffColumnKey,
              ): [count: number, ratio: number] => {
                const count = customerAttributeData.counts[columnKey]
                const ratio = customerAttributeData.ratios[columnKey]

                return [count, ratio]
              }

              return produce(
                {
                  subcategory: {
                    isHeader: true,
                    text: customerAttributeData.customerAttribute.name,
                    textCharacterLimit: 30,
                  },
                  ...mapCountColumnKey((countColumnKey): CellInfo => {
                    const [count, ratio] = getCountAndRatio(countColumnKey)
                    const text = formatCount(count, ratio)
                    const key = `customer-attribute-${customerAttributeData.customerAttribute.id}-${countColumnKey}`

                    return {
                      ratio,
                      text,
                      ...addHighlightProps(key),
                    }
                  }),
                  ...mapDiffColumnKey((diffColumnKey): CellInfo => {
                    const [count, ratio] = getCountAndRatio(diffColumnKey)
                    const [text, copyValue] = formatForDiff(count, ratio)
                    const { max, min } = diffMinMaxMap[diffColumnKey]
                    const key = `customer-attribute-diff-${customerAttributeData.customerAttribute.id}-${diffColumnKey}`

                    return {
                      align: 'center',
                      copyValue,
                      heatmap: true,
                      ratio: calcHeatmapRatio(ratio, max, min),
                      text,
                      ...addHighlightProps(key),
                    }
                  }),
                } as RowInfo<ColumnKey>,
                (draft) => {
                  if (customerAttributeDataIndex === 0) {
                    draft.category = {
                      isHeader: true,
                      text: customerAttributeData.customerAttributeGroup.name,
                      rowSpan:
                        customerAttributeData.customerAttributeGroup
                          .customerAttributes.length,
                    }
                  }
                },
              )
            },
          ),
        ],
      ),
    ]).flat()
    const countRowInfos = countRowInfosArray.flatMap((countRowInfos) => [
      ...countRowInfos,
      titleRowInfo,
    ])

    return [
      // ヘッダー
      produce(titleRowInfo, (draft) => {
        for (const columnCustomerAttribute of columnCustomerAttributes) {
          const cellInfo = draft[columnCustomerAttribute.id]
          if (exists(cellInfo)) {
            cellInfo.popup = (
              <TitleRowPopup customerAttribute={columnCustomerAttribute} />
            )
          }
        }
        for (const columnCustomerAttributesDiff of columnsDiffs) {
          const cellInfo = draft[getDiffKey(columnCustomerAttributesDiff)]
          if (exists(cellInfo)) {
            cellInfo.rowSpan = 3
          }
        }
      }),
      // 出現率
      {
        category: {
          colSpan: 2,
          isColumnHeader: true,
          isHeader: true,
          text: occurrenceRateLabel,
        },
        ...mapCountColumnKey((countColumnKey): CellInfo => {
          const totalCountData = findByPath(
            totalCountsData,
            'countColumnKey',
            countColumnKey,
          )
          const text = NumFormat.forRatio(totalCountData?.ratio ?? NaN)
          const key = `ratio-${countColumnKey}`

          return {
            text,
            ...addHighlightProps(key),
          }
        }),
      },
      // サンプル数
      {
        category: {
          colSpan: 2,
          isColumnHeader: true,
          isHeader: true,
          text: sampleSizeLabel,
        },
        ...mapCountColumnKey((countColumnKey): CellInfo => {
          const totalCountData = findByPath(
            totalCountsData,
            'countColumnKey',
            countColumnKey,
          )
          const text = NumFormat.format(totalCountData?.count ?? NaN)
          const key = `sample-${countColumnKey}`

          return {
            text,
            ...addHighlightProps(key),
          }
        }),
      },
      ...countRowInfos,
    ]
  }, [
    addHighlightProps,
    answersArrayData,
    brandRowInfoMap,
    brands,
    columnCustomerAttributes,
    columnsDiffs,
    customerAttributesArrayData,
    diffMinMaxMap,
    formatCount,
    formatForDiff,
    hiddenRowIds,
    mapCountColumnKey,
    mapDiffColumnKey,
    segmentDisplayType,
    occurrenceRateLabel,
    sampleSizeLabel,
    titleRowInfo,
    totalCountsData,
  ])
}
